import React, { useMemo, useState } from "react";
import { Checkbox, Input, Spin, Empty } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import styles from "./style.module.scss";

/**
 * Inline searchable topic list with checkboxes (Report Scope / Welcome topic steps).
 * Options: { id, label }[]
 * value: id[] (primitive ids matching option.id)
 */
const SearchableTopicChecklist = ({
  options = [],
  value = [],
  onChange,
  loading = false,
  placeholder,
  className = "",
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const selectedSet = useMemo(
    () => new Set((Array.isArray(value) ? value : []).map((v) => String(v))),
    [value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) =>
      String(o.label ?? "")
        .toLowerCase()
        .includes(q),
    );
  }, [options, query]);

  const allFilteredIds = filtered.map((o) => o.id);
  const allSelected =
    allFilteredIds.length > 0 &&
    allFilteredIds.every((id) => selectedSet.has(String(id)));
  const someSelected =
    !allSelected &&
    allFilteredIds.some((id) => selectedSet.has(String(id)));

  const toggleId = (id, checked) => {
    const sid = String(id);
    const next = new Set(selectedSet);
    if (checked) next.add(sid);
    else next.delete(sid);
    if (onChange) {
      onChange(
        options
          .filter((o) => next.has(String(o.id)))
          .map((o) => o.id),
      );
    }
  };

  const toggleSelectAllFiltered = () => {
    if (allFilteredIds.length === 0) return;
    const next = new Set(selectedSet);
    if (allSelected) {
      allFilteredIds.forEach((id) => next.delete(String(id)));
    } else {
      allFilteredIds.forEach((id) => next.add(String(id)));
    }
    if (onChange) {
      onChange(
        options
          .filter((o) => next.has(String(o.id)))
          .map((o) => o.id),
      );
    }
  };

  const selectedOptions = useMemo(
    () => options.filter((o) => selectedSet.has(String(o.id))),
    [options, selectedSet],
  );

  return (
    <div className={`${styles.wrap} ${className}`.trim()}>
      <Input
        allowClear
        size="large"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={
          placeholder || t("welcome.searchAvailableTopics")
        }
        prefix={<SearchOutlined className={styles.searchIcon} />}
        className={styles.search}
      />
      <div className={styles.listScroll}>
        {loading ? (
          <div className={styles.spinWrap}>
            <Spin />
          </div>
        ) : options.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("common.no_data")} />
        ) : (
          <>
            {filtered.length > 0 && (
              <label className={styles.selectAllRow}>
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={toggleSelectAllFiltered}
                />
                <span className={styles.selectAllLabel}>{t("filter.8")}</span>
              </label>
            )}
            {filtered.length === 0 ? (
              <div className={styles.noMatch}>{t("common.no_data")}</div>
            ) : (
              <ul className={styles.list}>
                {filtered.map((opt) => {
                  const id = opt.id;
                  const checked = selectedSet.has(String(id));
                  return (
                    <li key={String(id)} className={styles.row}>
                      <label className={styles.rowLabel}>
                        <Checkbox
                          checked={checked}
                          onChange={(e) => toggleId(id, e.target.checked)}
                        />
                        <span className={styles.rowText}>{opt.label}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </div>
      {selectedOptions.length > 0 && (
        <div className={styles.selectedSummary}>
          <span className={styles.selectedSummaryLabel}>
            {t("welcome.selectedTopicsLabel", { count: selectedOptions.length })}
          </span>
          <div className={styles.selectedChips}>
            {selectedOptions.map((opt) => (
              <span key={String(opt.id)} className={styles.selectedChip}>
                {opt.label}
                <button
                  className={styles.chipRemove}
                  onClick={() => toggleId(opt.id, false)}
                  aria-label={`Remove ${opt.label}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableTopicChecklist;
